import { useRef, useEffect } from "react";

/**
 * Flushes a pending value on unmount when the component hasn't had a chance
 * to fire its blur handler (e.g. user switches tabs). Only fires if the
 * current value differs from the last-flushed value.
 *
 * Returns `markFlushed` — call it inside onBlur after emitting the value
 * so the unmount cleanup knows it's already been saved.
 */
export function useFlushOnUnmount(
  currentRef: React.MutableRefObject<string>,
  onFlush: (value: string) => void,
) {
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;
  const flushedRef = useRef(currentRef.current);

  const markFlushed = () => {
    flushedRef.current = currentRef.current;
  };

  useEffect(() => {
    return () => {
      // Reading the ref AT UNMOUNT is the whole hook: the last value the user
      // typed is exactly what has not been flushed yet. Copying it into the
      // effect body — what the rule suggests — would flush the value as of
      // MOUNT, which is never the one at risk.
      if (currentRef.current !== flushedRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reading the ref at unmount is the hook
        onFlushRef.current(currentRef.current);
      }
    };
  }, [currentRef]);

  return markFlushed;
}
